import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pedService } from '../services/endpoints/pedService';
import { showService } from '../services/endpoints/showService';
import { seasonService } from '../services/endpoints/seasonService';
import { useCoachView } from '../contexts/CoachViewContext';
import { PEDCycle, PEDCompound, CompoundType, PhaseType, Show, SeasonStatus } from '../types';
import { addDays, differenceInDays, differenceInWeeks, parseISO, subDays } from 'date-fns';
import { format } from 'date-fns';
import LogFormModal from '../components/ped/LogFormModal';
import PurchaseCalculator from '../components/budget/PurchaseCalculator';
import CoachCommentThread from '../components/coach/CoachCommentThread';

const COMPOUND_GROUP_LABELS: Record<CompoundType, string> = {
  [CompoundType.InjectableAAS]: '💉 Injectable AAS',
  [CompoundType.OralAAS]:       '💊 Oral AAS',
  [CompoundType.Ancillary]:     '🛡️ Ancillaries',
  [CompoundType.Peptide]:       '🔬 Peptides',
};
const COMPOUND_TYPE_ORDER: CompoundType[] = [
  CompoundType.InjectableAAS,
  CompoundType.OralAAS,
  CompoundType.Ancillary,
  CompoundType.Peptide,
];

const PEDTracking: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'cycles' | 'logs' | 'planner'>('logs');
  const [plannerSubTab, setPlannerSubTab] = useState<'timeline' | 'calculator'>('timeline');
  const [calcCycleId, setCalcCycleId] = useState<string>('');
  const [showCycleForm, setShowCycleForm] = useState(false);
  const [editingCycle, setEditingCycle] = useState<any>(null);
  const queryClient = useQueryClient();
  const { viewAs, isCoachView } = useCoachView();

  const { data: compounds = [] } = useQuery({
    queryKey: ['ped-compounds'],
    queryFn: pedService.getCompounds,
  });

  const { data: cycles = [] } = useQuery<PEDCycle[]>({
    queryKey: ['ped-cycles', viewAs],
    queryFn: () => pedService.getCycles(viewAs ?? undefined),
  });

  const sortedCycles = [...cycles].sort((a, b) => a.startDate.localeCompare(b.startDate));

  const { data: activeCycle } = useQuery({
    queryKey: ['ped-active-cycle'],
    queryFn: pedService.getActiveCycle,
  });

  const { data: activeShow } = useQuery({
    queryKey: ['active-show', viewAs],
    queryFn: async () => {
      const shows = await showService.getShows(viewAs ?? undefined);
      if (!shows || shows.length === 0) return null;
      const today = new Date();
      const future = shows.filter(s => new Date(s.date) >= today)
                          .sort((a, b) => a.date.localeCompare(b.date));
      return future.length > 0
        ? future[0]
        : shows.sort((a, b) => b.date.localeCompare(a.date))[0];
    },
    staleTime: 5 * 60_000,
  });

  const deleteCycleMutation = useMutation({
    mutationFn: pedService.deleteCycle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ped-cycles'] });
      queryClient.invalidateQueries({ queryKey: ['ped-active-cycle'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const handleDeleteCycle = async (id: string, name: string) => {
    if (window.confirm(`Delete cycle "${name}"?`)) {
      deleteCycleMutation.mutate(id);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gold-500 mb-2">PED Cycle Tracking</h1>
          <p className="text-gray-400">Manage your cycles and log daily doses</p>
        </div>
        {activeTab === 'cycles' && !isCoachView && (
          <button
            onClick={() => setShowCycleForm(true)}
            className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-2 px-6 rounded transition-colors"
          >
            + Create Cycle
          </button>
        )}
      </div>

      {/* Active Cycle Card */}
      {activeCycle && (
        <div className="bg-gradient-to-r from-gold-900/30 to-dark-800 border border-gold-500/30 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gold-400 mb-2">Active Cycle</h2>
              <h3 className="text-2xl font-bold text-white mb-3">{activeCycle.name}</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Started:</span>
                  <span className="ml-2 text-white">{format(new Date(activeCycle.startDate), 'MMM d, yyyy')}</span>
                </div>
                {activeCycle.endDate && (
                  <div>
                    <span className="text-gray-400">Ends:</span>
                    <span className="ml-2 text-white">{format(new Date(activeCycle.endDate), 'MMM d, yyyy')}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-gold-500/20 px-4 py-2 rounded-lg">
              <div className="text-xs text-gold-400 uppercase">Compounds</div>
              <div className="text-2xl font-bold text-gold-300">{activeCycle.details?.length || 0}</div>
            </div>
          </div>
          {activeCycle.details && activeCycle.details.length > 0 && (
            <div className="mt-4 space-y-2">
              {activeCycle.details.map((detail) => (
                <div key={detail.cycleDetailId} className="flex justify-between items-center bg-dark-700 rounded px-4 py-2">
                  <div>
                    <span className="font-semibold text-white">{detail.compound?.name}</span>
                    <span className="text-gray-400 ml-2">({detail.compound?.shortName})</span>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-semibold">{detail.weeklyTotal}{detail.compound?.unit || 'mg'}/week</div>
                    <div className="text-xs text-gray-400">{detail.doseAmount}{detail.compound?.unit || 'mg'} {detail.frequency}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-dark-700">
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-3 font-medium transition-colors ${
            activeTab === 'logs'
              ? 'text-gold-500 border-b-2 border-gold-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          📋 Daily Logs
        </button>
        <button
          onClick={() => setActiveTab('cycles')}
          className={`px-4 py-3 font-medium transition-colors ${
            activeTab === 'cycles'
              ? 'text-gold-500 border-b-2 border-gold-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          💉 Cycles
        </button>
        <button
          onClick={() => setActiveTab('planner')}
          className={`px-4 py-3 font-medium transition-colors ${
            activeTab === 'planner'
              ? 'text-gold-500 border-b-2 border-gold-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          📅 Planner
        </button>
      </div>

      {/* Cycles Tab */}
      {activeTab === 'cycles' && (
        <div className="space-y-4">
          {cycles.length === 0 ? (
            <div className="bg-dark-800 rounded-lg p-8 text-center">
              <p className="text-gray-400 mb-4">No cycles yet</p>
          {!isCoachView && (
              <button
                onClick={() => setShowCycleForm(true)}
                className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-2 px-6 rounded transition-colors"
              >
                Create Your First Cycle
              </button>
            )}
            </div>
          ) : (
            sortedCycles.map((cycle) => (
              <div key={cycle.cycleId} className="bg-dark-800 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{cycle.name}</h3>
                    <div className="flex gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Start:</span>
                        <span className="ml-2 text-white">{format(new Date(cycle.startDate), 'MMM d, yyyy')}</span>
                      </div>
                      {cycle.endDate && (
                        <div>
                          <span className="text-gray-400">End:</span>
                          <span className="ml-2 text-white">{format(new Date(cycle.endDate), 'MMM d, yyyy')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {!isCoachView && (
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setEditingCycle(cycle);
                          setShowCycleForm(true);
                        }}
                        className="text-blue-400 hover:text-blue-300 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCycle(cycle.cycleId, cycle.name)}
                        className="text-red-400 hover:text-red-300 font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                {cycle.details && cycle.details.length > 0 && (
                  <div className="space-y-2">
                    {cycle.details.map((detail) => (
                      <div key={detail.cycleDetailId} className="flex justify-between items-center bg-dark-700 rounded px-4 py-2">
                        <span className="text-white">{detail.compound?.name} ({detail.compound?.shortName})</span>
                        <span className="text-gold-400 font-semibold">{detail.weeklyTotal}{detail.compound?.unit || 'mg'}/week</span>
                      </div>
                    ))}
                  </div>
                )}
                {cycle.notes && (
                  <p className="mt-3 text-sm text-gray-400 italic">{cycle.notes}</p>
                )}
                <CoachCommentThread
                  contextType="PEDCycle"
                  contextId={cycle.cycleId}
                  label="cycle"
                />
              </div>
            ))
          )}
        </div>
      )}

      {/* Daily Logs Tab */}
      {activeTab === 'logs' && (
        <DailyLogsSection compounds={compounds} activeCycle={activeCycle ?? undefined} />
      )}

      {/* Planner Tab */}
      {activeTab === 'planner' && (
        <div>
          {/* Planner sub-tabs */}
          <div className="flex gap-1 mb-6 bg-gray-800 rounded-lg p-1 w-fit">
            <button
              onClick={() => setPlannerSubTab('timeline')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                plannerSubTab === 'timeline'
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              📊 Cycle Planner
            </button>
            <button
              onClick={() => !isCoachView && setPlannerSubTab('calculator')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isCoachView ? 'hidden' : ''} ${
                plannerSubTab === 'calculator'
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              🛒 Purchase Calculator
            </button>
          </div>

          {plannerSubTab === 'timeline' && (
            <CyclePlannerSection cycles={sortedCycles} activeShow={activeShow ?? null} />
          )}

          {plannerSubTab === 'calculator' && !isCoachView && (
            <PurchaseCalculator
              cycle={sortedCycles.find(c => c.cycleId === calcCycleId) ?? (activeCycle ?? null)}
              allCycles={sortedCycles}
              onCycleChange={(id) => setCalcCycleId(id)}
            />
          )}
        </div>
      )}

      {/* Cycle Form Modal */}
      {showCycleForm && (
        <CycleFormModal
          compounds={compounds}
          editCycle={editingCycle}
          onClose={() => {
            setShowCycleForm(false);
            setEditingCycle(null);
          }}
          onSuccess={() => {
            setShowCycleForm(false);
            setEditingCycle(null);
            queryClient.invalidateQueries({ queryKey: ['ped-cycles'] });
            queryClient.invalidateQueries({ queryKey: ['ped-active-cycle'] });
          }}
        />
      )}
    </div>
  );
};

// ── Cycle Planner Section ────────────────────────────────────────────────────
interface CyclePlannerSectionProps {
  cycles: PEDCycle[];
  activeShow: Show | null;
}

// Phase colour palette (inline styles — Tailwind can't handle fully-dynamic colours)
const PHASE_COLORS: Record<PhaseType, { bg: string; text: string }> = {
  [PhaseType.Cruise]:      { bg: '#1e3a5f', text: '#93c5fd' },
  [PhaseType.MiniCut]:     { bg: '#3b1a1a', text: '#fca5a5' },
  [PhaseType.Hold]:        { bg: '#2b3040', text: '#cbd5e1' },
  [PhaseType.Bridge]:      { bg: '#2e1e47', text: '#c4b5fd' },
  [PhaseType.Rebound]:     { bg: '#0e3538', text: '#5eead4' },
  [PhaseType.GrowthBlast]: { bg: '#14352a', text: '#6ee7b7' },
  [PhaseType.ContestPrep]: { bg: '#3b1500', text: '#fed7aa' },
  [PhaseType.Show]:        { bg: '#3d2e00', text: '#fbbf24' },
};

const PHASE_LABEL: Record<PhaseType, string> = {
  [PhaseType.Cruise]:      'Cruise',
  [PhaseType.MiniCut]:     'Mini Cut',
  [PhaseType.Hold]:        'Hold',
  [PhaseType.Bridge]:      'Bridge',
  [PhaseType.Rebound]:     'Rebound',
  [PhaseType.GrowthBlast]: 'Growth Blast',
  [PhaseType.ContestPrep]: 'Contest Prep',
  [PhaseType.Show]:        'Show Day',
};

const CyclePlannerSection: React.FC<CyclePlannerSectionProps> = ({ cycles, activeShow }) => {
  const today = new Date();
  const showDate    = activeShow ? parseISO(activeShow.date) : null;
  const weeksToShow = showDate ? differenceInWeeks(showDate, today) : null;
  const daysToShow  = showDate ? differenceInDays(showDate, today)  : null;
  const phases      = [...(activeShow?.phases ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

  const overBudgetCycles = showDate
    ? cycles.filter((c) => c.endDate && parseISO(c.endDate) > showDate)
    : [];

  // ── Gantt maths ─────────────────────────────────────────────────────────
  const allStartDates: Date[] = [
    today,
    ...cycles.map(c => parseISO(c.startDate)),
    ...phases.map(p => parseISO(p.startDate)),
  ];
  const timelineStart = new Date(Math.min(...allStartDates.map(d => d.getTime())));
  const timelineEnd   = showDate ?? addDays(today, 91);
  const totalDays     = Math.max(1, differenceInDays(timelineEnd, timelineStart));

  const toPct = (d: Date) =>
    Math.max(0, Math.min(100, (differenceInDays(d, timelineStart) / totalDays) * 100));

  const toWidthPct = (s: Date, e: Date) =>
    Math.max(0.5, toPct(e) - toPct(s));

  const todayPct = toPct(today);

  // Infer which phase(s) a cycle spans (used in card view)
  const getCyclePhaseLabel = (cycle: PEDCycle): string | null => {
    if (!phases.length) return null;
    if (cycle.phaseId) {
      const p = phases.find(ph => ph.phaseId === cycle.phaseId);
      if (p) return p.name;
    }
    const cs = parseISO(cycle.startDate);
    const ce = cycle.endDate ? parseISO(cycle.endDate) : null;
    const overlapping = phases.filter(p => {
      const ps = parseISO(p.startDate);
      const pe = parseISO(p.endDate);
      return ce ? (cs <= pe && ce >= ps) : (cs >= ps && cs <= pe);
    });
    if (!overlapping.length) return null;
    return overlapping.length === 1 ? overlapping[0].name : overlapping.map(p => p.name).join(' → ');
  };

  return (
    <div className="space-y-6">

      {/* ── Show context bar ─────────────────────────────────────────────── */}
      {activeShow && showDate ? (
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Active Show</p>
              <p className="text-white font-bold text-lg">{activeShow.name}</p>
              <p className="text-gold-400 text-sm">{format(showDate, 'MMMM d, yyyy')}</p>
            </div>
            <div className="flex gap-6 text-center">
              <div>
                <p className="text-3xl font-bold text-gold-400">{Math.max(0, weeksToShow!)}</p>
                <p className="text-xs text-gray-400">weeks out</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{Math.max(0, daysToShow!)}</p>
                <p className="text-xs text-gray-400">days out</p>
              </div>
            </div>
          </div>
          {overBudgetCycles.length > 0 && (
            <div className="mt-4 bg-red-900/20 border border-red-500/30 rounded-lg px-4 py-3">
              <p className="text-red-400 text-sm font-semibold">
                ⚠️ {overBudgetCycles.length} cycle{overBudgetCycles.length > 1 ? 's extend' : ' extends'} past show date:
              </p>
              <ul className="mt-1 space-y-0.5">
                {overBudgetCycles.map((c) => {
                  const overshootDays = differenceInDays(parseISO(c.endDate!), showDate!);
                  return (
                    <li key={c.cycleId} className="text-red-300 text-sm">
                      • {c.name} — ends {format(parseISO(c.endDate!), 'MMM d')} ({overshootDays} day{overshootDays !== 1 ? 's' : ''} past show)
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-5 text-center text-gray-400">
          <p>No active show found. Create a show to see cycle planning.</p>
        </div>
      )}

      {/* ── Gantt Timeline ───────────────────────────────────────────────── */}
      {activeShow && showDate && (
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-3">📊 Phase & Cycle Timeline</h3>

          {/* Date ruler */}
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{format(timelineStart, 'MMM d')}</span>
            <span className="text-gray-600">↑ Today</span>
            <span>{format(timelineEnd, 'MMM d, yyyy')}</span>
          </div>

          {/* Phase bands */}
          {phases.length > 0 ? (
            <div className="relative h-9 rounded overflow-hidden mb-3" style={{ backgroundColor: '#1a1a2e' }}>
              {phases.map((phase) => {
                const ps   = parseISO(phase.startDate);
                const pe   = parseISO(phase.endDate);
                const left  = toPct(ps);
                const width = toWidthPct(ps, pe);
                const clr   = PHASE_COLORS[phase.phaseType] ?? { bg: '#2a2a2a', text: '#aaa' };
                return (
                  <div
                    key={phase.phaseId}
                    className="absolute top-0 h-full flex items-center px-2 overflow-hidden"
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      backgroundColor: clr.bg,
                      borderRight: '1px solid rgba(0,0,0,0.4)',
                    }}
                    title={`${phase.name}: ${format(ps, 'MMM d')} – ${format(pe, 'MMM d')}`}
                  >
                    <span className="text-xs font-medium truncate" style={{ color: clr.text }}>
                      {PHASE_LABEL[phase.phaseType] ?? phase.name}
                    </span>
                  </div>
                );
              })}
              {/* Today marker */}
              <div
                className="absolute top-0 h-full z-20 pointer-events-none"
                style={{ left: `${todayPct}%`, width: '2px', backgroundColor: 'rgba(255,255,255,0.7)' }}
              />
            </div>
          ) : (
            /* No phases yet — just a ruler with today marker */
            <div className="relative h-3 rounded mb-3" style={{ backgroundColor: '#2a2a3e' }}>
              <div
                className="absolute top-0 h-full z-20"
                style={{ left: `${todayPct}%`, width: '2px', backgroundColor: 'rgba(255,255,255,0.5)' }}
              />
            </div>
          )}

          {/* Cycle bars */}
          {cycles.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-3">
              No cycles yet — create one to see it on the timeline.
            </p>
          ) : (
            <div className="space-y-1.5">
              {cycles.map((cycle) => {
                const cs        = parseISO(cycle.startDate);
                const ce        = cycle.endDate ? parseISO(cycle.endDate) : addDays(cs, 28);
                const left      = toPct(cs);
                const width     = toWidthPct(cs, ce);
                const exceeds   = showDate && cycle.endDate && parseISO(cycle.endDate) > showDate;
                return (
                  <div key={cycle.cycleId} className="relative h-8">
                    <div className="absolute inset-0 rounded" style={{ backgroundColor: '#1e1e2e' }} />
                    <div
                      className="absolute top-1 h-6 rounded flex items-center px-2 overflow-hidden"
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                        minWidth: '3%',
                        backgroundColor: exceeds ? '#7f1d1d' : '#78350f',
                        border:          `1px solid ${exceeds ? '#ef4444' : '#d97706'}`,
                      }}
                      title={`${cycle.name}: ${format(cs, 'MMM d')} – ${cycle.endDate ? format(ce, 'MMM d') : 'ongoing'}`}
                    >
                      <span className="text-xs text-white font-medium truncate">{cycle.name}</span>
                    </div>
                    {/* Today marker */}
                    <div
                      className="absolute top-0 h-full z-10 pointer-events-none"
                      style={{ left: `${todayPct}%`, width: '2px', backgroundColor: 'rgba(255,255,255,0.2)' }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Phase legend */}
          {phases.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3 pt-3 border-t border-dark-700">
              {phases.map((phase) => {
                const clr = PHASE_COLORS[phase.phaseType] ?? { bg: '#2a2a2a', text: '#aaa' };
                return (
                  <div key={phase.phaseId} className="flex items-center gap-1.5 text-xs">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: clr.bg, border: `1px solid ${clr.text}` }} />
                    <span style={{ color: clr.text }}>{phase.name}</span>
                    <span className="text-gray-600">
                      ({format(parseISO(phase.startDate), 'MMM d')}–{format(parseISO(phase.endDate), 'MMM d')})
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Cycle detail cards ───────────────────────────────────────────── */}
      {cycles.length > 0 && (
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4">Cycle Details</h3>
          <div className="space-y-4">
            {cycles.map((cycle) => {
              const startDate  = parseISO(cycle.startDate);
              const endDate    = cycle.endDate ? parseISO(cycle.endDate) : null;
              const cycleWeeks = endDate ? Math.round(differenceInDays(endDate, startDate) / 7) : null;
              const exceedsShow    = showDate && endDate && endDate > showDate;
              const weeksFromToday = differenceInWeeks(startDate, today);
              const phaseLabel     = getCyclePhaseLabel(cycle);

              return (
                <div
                  key={cycle.cycleId}
                  className={`border rounded-lg p-4 ${
                    exceedsShow ? 'border-red-500/40 bg-red-900/10' : 'border-dark-600 bg-dark-700/50'
                  }`}
                >
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div>
                      <h4 className="text-white font-semibold">{cycle.name}</h4>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {format(startDate, 'MMM d, yyyy')}
                        {endDate && ` → ${format(endDate, 'MMM d, yyyy')}`}
                        {cycleWeeks !== null && ` (${cycleWeeks} wks)`}
                      </p>
                      {phaseLabel && (
                        <p className="text-xs text-gold-400 mt-1">📍 {phaseLabel}</p>
                      )}
                    </div>
                    <div className="flex gap-3 text-right text-xs">
                      {weeksToShow !== null && (
                        <div>
                          <p className="text-gray-400">Status</p>
                          <p className={`font-semibold ${weeksFromToday < 0 ? 'text-green-400' : 'text-gold-400'}`}>
                            {weeksFromToday < 0 ? 'Running' : `Starts Wk ${weeksFromToday}`}
                          </p>
                        </div>
                      )}
                      {exceedsShow && endDate && showDate && (
                        <div>
                          <p className="text-red-400 font-semibold">
                            +{differenceInDays(endDate, showDate)}d past show
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {cycle.details && cycle.details.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {cycle.details.map((d) => (
                        <span key={d.cycleDetailId} className="text-xs bg-dark-600 text-gray-300 rounded-full px-3 py-1">
                          {d.compound?.shortName} {d.weeklyTotal}{d.compound?.unit}/wk
                        </span>
                      ))}
                    </div>
                  )}
                  <CoachCommentThread
                    contextType="PEDCycle"
                    contextId={cycle.cycleId}
                    label="cycle"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Cycle Form Modal ──────────────────────────────────────────────────────────
interface CycleFormModalProps {
  compounds: PEDCompound[];
  editCycle?: any;
  onClose: () => void;
  onSuccess: () => void;
}

const CycleFormModal: React.FC<CycleFormModalProps> = ({ compounds, editCycle, onClose, onSuccess }) => {
  const isEditing = !!editCycle;
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: seasons = [] } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => seasonService.getSeasons(),
  });

  const [formData, setFormData] = useState(() => {
    if (editCycle) {
      return {
        name:      editCycle.name || '',
        startDate: editCycle.startDate ? editCycle.startDate.split('T')[0] : format(new Date(), 'yyyy-MM-dd'),
        endDate:   editCycle.endDate   ? editCycle.endDate.split('T')[0]   : '',
        notes:     editCycle.notes || '',
        seasonId:  editCycle.seasonId || '',
        phaseId:   editCycle.phaseId || '',
        details: (editCycle.details || []).map((d: any) => ({
          compoundId:  d.compoundId || d.compound?.compoundId || '',
          doseAmount:  d.doseAmount  || 0,
          frequency:   d.frequency   || 'E3.5D',
          weeklyTotal: d.weeklyTotal || 0,
        })),
      };
    }
    return {
      name:      '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate:   '',
      notes:     '',
      seasonId:  '',
      phaseId:   '',
      details:   [] as any[],
    };
  });

  const { data: seasonPhases = [] } = useQuery({
    queryKey: ['season-phases', formData.seasonId],
    queryFn: () => seasonService.getSeasonPhases(formData.seasonId),
    enabled: !!formData.seasonId,
  });

  const createCycleMutation = useMutation({
    mutationFn: pedService.createCycle,
    onSuccess: () => {
      setStatusMessage({ type: 'success', text: 'Cycle created successfully!' });
      setTimeout(() => onSuccess(), 1500);
    },
    onError: (error) => {
      setStatusMessage({ type: 'error', text: 'Failed to create cycle. Please try again.' });
      console.error(error);
    },
  });

  const updateCycleMutation = useMutation({
    mutationFn: (data: any) => pedService.updateCycle(editCycle.cycleId, data),
    onSuccess: () => {
      setStatusMessage({ type: 'success', text: 'Cycle updated successfully!' });
      setTimeout(() => onSuccess(), 1500);
    },
    onError: (error) => {
      setStatusMessage({ type: 'error', text: 'Failed to update cycle. Please try again.' });
      console.error(error);
    },
  });

  const addCompound = () => {
    setFormData({
      ...formData,
      details: [
        ...formData.details,
        {
          compoundId: '',
          doseAmount: 0,
          frequency: 'E3.5D',
          weeklyTotal: 0,
        },
      ],
    });
  };

  const removeCompound = (index: number) => {
    setFormData({
      ...formData,
      details: formData.details.filter((_: any, i: number) => i !== index),
    });
  };

  const updateCompound = (index: number, field: string, value: any) => {
    const newDetails = [...formData.details];
    newDetails[index] = { ...newDetails[index], [field]: value };

    // Auto-calculate per-dose amount based on weekly total and frequency
    if (field === 'weeklyTotal' || field === 'frequency') {
      const weekly = field === 'weeklyTotal' ? value : newDetails[index].weeklyTotal;
      const freq   = field === 'frequency'   ? value : newDetails[index].frequency;

      let divisor = 1;
      switch (freq) {
        case 'ED':    divisor = 7;    break;
        case 'EOD':   divisor = 3.5;  break;
        case 'E3D':   divisor = 2.33; break;
        case 'E3.5D': divisor = 2;    break;
        case 'E7D':   divisor = 1;    break;
      }

      newDetails[index].doseAmount = parseFloat((weekly / divisor).toFixed(1));
    }

    setFormData({ ...formData, details: newDetails });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert empty strings to undefined so the API ignores them
    const submitData = {
      ...formData,
      endDate:  formData.endDate   || undefined,
      seasonId: formData.seasonId  || undefined,
      showId:   undefined,           // no longer set from this form; legacy column removed in v4.1
      phaseId:  formData.phaseId   || undefined,
    };
    
    if (isEditing) {
      updateCycleMutation.mutate(submitData);
    } else {
      createCycleMutation.mutate(submitData);
    }
  };

  const isSaving = createCycleMutation.isPending || updateCycleMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-dark-700 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gold-500">{isEditing ? 'Edit Cycle' : 'Create New Cycle'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Cycle Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 w-full"
              placeholder="e.g., Test/Deca Blast"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">End Date (Optional)</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 w-full"
              />
            </div>
          </div>

          {/* Season selector */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Season <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <select
              value={formData.seasonId}
              onChange={(e) => setFormData({ ...formData, seasonId: e.target.value })}
              className="bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 w-full"
            >
              <option value="">— No season assigned —</option>
              {seasons
                .filter(s => s.status !== SeasonStatus.Archived)
                .map(s => (
                  <option key={s.seasonId} value={s.seasonId}>
                    {s.name}
                    {s.endDate ? ` (ends ${format(parseISO(s.endDate), 'MMM d, yyyy')})` : ''}
                  </option>
                ))}
            </select>
          </div>

          {/* Phase assignment (optional) */}
          {seasonPhases.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Assign to Phase <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <select
                value={formData.phaseId}
                onChange={(e) => {
                  const selectedPhaseId = e.target.value;
                  const selectedPhase   = seasonPhases.find(p => p.phaseId === selectedPhaseId);
                  setFormData({
                    ...formData,
                    phaseId:   selectedPhaseId,
                    // Snap dates to phase bounds when a phase is chosen
                    ...(selectedPhase && {
                      startDate: selectedPhase.startDate.split('T')[0],
                      endDate:   selectedPhase.endDate.split('T')[0],
                    }),
                  });
                }}
                className="bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 w-full"
              >
                <option value="">— No phase assigned —</option>
                {seasonPhases.map((phase) => (
                  <option key={phase.phaseId} value={phase.phaseId}>
                    {phase.name}
                    {' '}({format(parseISO(phase.startDate), 'MMM d')} – {format(parseISO(phase.endDate), 'MMM d')})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-300">Compounds</label>
              <button
                type="button"
                onClick={addCompound}
                className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-1 px-4 rounded text-sm"
              >
                + Add Compound
              </button>
            </div>

            {formData.details.length === 0 ? (
              <p className="text-gray-500 text-sm">No compounds added yet</p>
            ) : (
              <div className="space-y-3">
                {formData.details.map((detail: any, index: number) => (
                  <div key={index} className="bg-dark-700 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm font-medium text-gray-300">Compound {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeCompound(index)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Compound selector — full width */}
                      <select
                        value={detail.compoundId}
                        onChange={(e) => updateCompound(index, 'compoundId', e.target.value)}
                        className="bg-white border border-dark-500 text-black rounded px-3 py-2 col-span-2"
                        required
                      >
                        <option value="">Select compound...</option>
                        {COMPOUND_TYPE_ORDER.map((type) => {
                          const group = compounds.filter((c) => c.type === type);
                          if (group.length === 0) return null;
                          return (
                            <optgroup key={type} label={COMPOUND_GROUP_LABELS[type]}>
                              {group.map((c) => (
                                <option key={c.compoundId} value={c.compoundId}>
                                  {c.name} ({c.unit})
                                </option>
                              ))}
                            </optgroup>
                          );
                        })}
                      </select>

                      {/* ① Weekly total — primary input */}
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">
                          Weekly Total ({detail.compoundId ? compounds.find(c => c.compoundId === detail.compoundId)?.unit || 'mg' : 'mg'}/wk)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={detail.weeklyTotal || ''}
                          onChange={(e) => updateCompound(index, 'weeklyTotal', parseFloat(e.target.value) || 0)}
                          className="bg-white border border-dark-500 text-black rounded px-3 py-2 w-full"
                          placeholder="e.g. 500"
                          required
                        />
                      </div>

                      {/* ② Frequency */}
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Injection Frequency</label>
                        <select
                          value={detail.frequency}
                          onChange={(e) => updateCompound(index, 'frequency', e.target.value)}
                          className="bg-white border border-dark-500 text-black rounded px-3 py-2 w-full"
                        >
                          <option value="ED">Every Day (ED) — ÷7</option>
                          <option value="EOD">Every Other Day (EOD) — ÷3.5</option>
                          <option value="E3D">Every 3 Days (E3D) — ÷2.33</option>
                          <option value="E3.5D">Twice Weekly (E3.5D) — ÷2</option>
                          <option value="E7D">Once Weekly (E7D) — ÷1</option>
                        </select>
                      </div>
                    </div>

                    {/* ③ Calculated per-dose result */}
                    {detail.weeklyTotal > 0 && (
                      <div className="mt-3 flex items-center justify-between bg-dark-600 rounded-lg px-4 py-2">
                        <span className="text-gray-400 text-sm">Per injection</span>
                        <span className="text-gold-400 font-bold text-lg">
                          {detail.doseAmount.toFixed(1)}
                          {detail.compoundId ? compounds.find(c => c.compoundId === detail.compoundId)?.unit || 'mg' : 'mg'}
                          <span className="text-gray-500 text-xs font-normal ml-1">{detail.frequency}</span>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Notes (Optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="bg-dark-700 border border-dark-600 text-white rounded-lg px-4 py-2 w-full"
              placeholder="Add any notes about this cycle..."
            />
          </div>

          <div className="pt-4 space-y-3">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-dark-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-400 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : isEditing ? 'Update Cycle' : 'Create Cycle'}
              </button>
            </div>
            {statusMessage && (
              <div className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
                statusMessage.type === 'success'
                  ? 'bg-green-600/20 border border-green-600/30 text-green-400'
                  : 'bg-red-600/20 border border-red-600/30 text-red-400'
              }`}>
                <span>{statusMessage.type === 'success' ? '✅' : '❌'}</span>
                {statusMessage.text}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Daily Logs Section ────────────────────────────────────────────────────────
// "Edit Day" uses <LogFormModal initialDate={dateKey} /> — no separate DayEditModal needed.
// The LogFormModal auto-detects existing logs for the date and enters edit mode.

// Intentionally unreachable — silences TS "unused import" if pedService tree-shakes out
const _noop: React.FC<{ dateKey: string; compounds: PEDCompound[]; viewAs?: string | null; onClose: () => void; onSuccess: () => void }> = ({ dateKey, compounds, viewAs, onClose, onSuccess }) => {
  const [editedLogs, setEditedLogs] = useState<{
    logId: string;
    compoundId: string;
    compoundName: string;
    doseAmount: number | string;
    notes: string;
    _deleted: boolean;
  }[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);

  // Fetch the day's logs fresh from the API so we always have current values
  React.useEffect(() => {
    setIsLoadingLogs(true);
    pedService
      .getLogs({ startDate: dateKey, endDate: dateKey, viewAs: viewAs ?? undefined })
      .then((logs) => {
        setEditedLogs(
          logs.map((log: any) => ({
            logId:        log.logId,
            compoundId:   log.compoundId ?? log.compound?.compoundId ?? '',
            compoundName: log.compound?.name ?? '',
            doseAmount:   log.doseAmount ?? '',
            notes:        log.notes ?? '',
            _deleted:     false,
          })),
        );
      })
      .catch((err) => {
        console.error('Failed to load day logs:', err);
        setError('Failed to load dose data. Please close and try again.');
      })
      .finally(() => setIsLoadingLogs(false));
  }, [dateKey, viewAs]);

  const [newLogs, setNewLogs] = useState<{ compoundId: string; doseAmount: string; notes: string }[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError]       = useState<string | null>(null);

  const addNewRow    = () => setNewLogs((p) => [...p, { compoundId: '', doseAmount: '', notes: '' }]);
  const removeNewRow = (idx: number) => setNewLogs((p) => p.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      for (const log of editedLogs) {
        if (log._deleted) {
          await pedService.deleteLog(log.logId);
        } else {
          await pedService.updateLog(log.logId, {
            date:       dateKey,
            compoundId: log.compoundId,
            doseAmount: Number(log.doseAmount),
            notes:      log.notes || undefined,
          });
        }
      }
      for (const nl of newLogs) {
        if (!nl.compoundId || !nl.doseAmount) continue;
        await pedService.createLog({
          date:       dateKey,
          compoundId: nl.compoundId,
          doseAmount: Number(nl.doseAmount),
          notes:      nl.notes || undefined,
        });
      }
      onSuccess();
    } catch (err) {
      setError('Failed to save changes. Please try again.');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg max-w-xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="p-5 border-b border-dark-700 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gold-500">Edit Day's Doses</h2>
            <p className="text-sm text-gray-400 mt-0.5">
              {format(new Date(dateKey + 'T12:00:00'), 'EEEE, MMMM d, yyyy')}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">✕</button>
        </div>

      {/* Body */}
        <div className="p-5 space-y-3">
          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          {isLoadingLogs ? (
            <div className="py-8 text-center text-gray-400 text-sm">Loading dose data…</div>
          ) : editedLogs.length === 0 && newLogs.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">No doses found for this date.</div>
          ) : null}

          {/* Existing dose rows */}
          {editedLogs.map((log, idx) => (
            <div
              key={log.logId}
              className={`rounded-lg p-3 border transition-opacity ${
                log._deleted
                  ? 'border-red-500/30 bg-red-900/10 opacity-50'
                  : 'border-dark-600 bg-dark-700'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white mb-2">{log.compoundName}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Dose Amount</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={log.doseAmount}
                        disabled={log._deleted}
                        onChange={(e) => {
                          const updated = [...editedLogs];
                          updated[idx] = { ...updated[idx], doseAmount: e.target.value };
                          setEditedLogs(updated);
                        }}
                        className="bg-dark-600 border border-dark-500 text-white rounded px-3 py-1.5 w-full text-sm disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Notes</label>
                      <input
                        type="text"
                        value={log.notes}
                        disabled={log._deleted}
                        onChange={(e) => {
                          const updated = [...editedLogs];
                          updated[idx] = { ...updated[idx], notes: e.target.value };
                          setEditedLogs(updated);
                        }}
                        className="bg-dark-600 border border-dark-500 text-white rounded px-3 py-1.5 w-full text-sm disabled:opacity-50"
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const updated = [...editedLogs];
                    updated[idx] = { ...updated[idx], _deleted: !updated[idx]._deleted };
                    setEditedLogs(updated);
                  }}
                  className={`mt-6 flex-shrink-0 text-xs px-2 py-1 rounded font-medium transition-colors ${
                    log._deleted
                      ? 'bg-dark-600 text-gray-300 hover:bg-dark-500'
                      : 'text-red-400 hover:text-red-300'
                  }`}
                >
                  {log._deleted ? 'Restore' : 'Remove'}
                </button>
              </div>
            </div>
          ))}

          {/* New dose rows */}
          {newLogs.map((nl, idx) => (
            <div key={`new-${idx}`} className="rounded-lg p-3 border border-gold-500/30 bg-gold-900/10">
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Compound</label>
                    <select
                      value={nl.compoundId}
                      onChange={(e) => {
                        const updated = [...newLogs];
                        updated[idx] = { ...updated[idx], compoundId: e.target.value };
                        setNewLogs(updated);
                      }}
                      className="bg-white border border-dark-500 text-black rounded px-3 py-1.5 w-full text-sm"
                    >
                      <option value="">Select compound…</option>
                      {COMPOUND_TYPE_ORDER.map((type) => {
                        const group = compounds.filter((c) => c.type === type);
                        if (group.length === 0) return null;
                        return (
                          <optgroup key={type} label={COMPOUND_GROUP_LABELS[type]}>
                            {group.map((c) => (
                              <option key={c.compoundId} value={c.compoundId}>{c.name}</option>
                            ))}
                          </optgroup>
                        );
                      })}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Dose Amount</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={nl.doseAmount}
                        onChange={(e) => {
                          const updated = [...newLogs];
                          updated[idx] = { ...updated[idx], doseAmount: e.target.value };
                          setNewLogs(updated);
                        }}
                        className="bg-dark-600 border border-dark-500 text-white rounded px-3 py-1.5 w-full text-sm"
                        placeholder="e.g. 250"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Notes</label>
                      <input
                        type="text"
                        value={nl.notes}
                        onChange={(e) => {
                          const updated = [...newLogs];
                          updated[idx] = { ...updated[idx], notes: e.target.value };
                          setNewLogs(updated);
                        }}
                        className="bg-dark-600 border border-dark-500 text-white rounded px-3 py-1.5 w-full text-sm"
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeNewRow(idx)}
                  className="mt-6 flex-shrink-0 text-red-400 hover:text-red-300 text-xs px-2 py-1"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          {/* Add row */}
          <button
            onClick={addNewRow}
            className="w-full py-2 border border-dashed border-dark-600 text-gray-500 hover:text-gray-300 hover:border-dark-500 rounded-lg text-sm transition-colors"
          >
            + Add Another Dose
          </button>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-dark-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-dark-700 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-5 py-2 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-400 disabled:opacity-50 text-sm"
          >
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Daily Logs Section Component
interface DailyLogsSectionProps {
  compounds: PEDCompound[];
  activeCycle?: import('../types').PEDCycle | null;
}

const HISTORY_PAGE_SIZE = 25;

const DailyLogsSection: React.FC<DailyLogsSectionProps> = ({ compounds, activeCycle }) => {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [showLogForm, setShowLogForm] = useState(false);
  const [editingLog, setEditingLog] = useState<any>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [editingDayKey, setEditingDayKey] = useState<string | null>(null);

  const toggleDate = (dateKey: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };
  const queryClient = useQueryClient();
  const { viewAs, isCoachView } = useCoachView();

  // History date range — defaults to last 30 days
  const [historyDateFrom, setHistoryDateFrom] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [historyDateTo, setHistoryDateTo]     = useState(() => format(new Date(), 'yyyy-MM-dd'));
  // Applied range — only updated on "Apply" so query doesn't fire on every keystroke
  const [appliedFrom, setAppliedFrom] = useState(historyDateFrom);
  const [appliedTo, setAppliedTo]     = useState(historyDateTo);

  // Get logs for selected date (for the "Log Today's Doses" quick section)
  const { data: logs = [] } = useQuery({
    queryKey: ['ped-logs', selectedDate],
    queryFn: () => pedService.getLogsByDate(selectedDate),
  });

  // Get logs for history section (viewAs-aware, uses applied date range)
  const { data: recentLogs = [] } = useQuery({
    queryKey: ['ped-logs-recent', viewAs, appliedFrom, appliedTo],
    queryFn: () => pedService.getLogs({
      startDate: appliedFrom,
      endDate: appliedTo,
      viewAs: viewAs ?? undefined,
    }),
  });

  const deleteLogMutation = useMutation({
    mutationFn: pedService.deleteLog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ped-logs'] });
      queryClient.invalidateQueries({ queryKey: ['ped-logs-recent'] });
      setConfirmDeleteId(null);
    },
  });

  const handleApplyDateRange = () => {
    setAppliedFrom(historyDateFrom);
    setAppliedTo(historyDateTo);
    setHistoryCurrentPage(1);
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      // Fetch ALL logs — no date filter — for a complete export
      const allLogs = await pedService.getLogs({ viewAs: viewAs ?? undefined });

      if (allLogs.length === 0) {
        setIsExporting(false);
        return;
      }

      const headers = ['Date', 'Compound', 'Short Name', 'Unit', 'Dose Amount', 'Notes'];
      const rows = allLogs.map((log: any) => [
        log.date.split('T')[0],
        log.compound?.name ?? '',
        log.compound?.shortName ?? '',
        log.compound?.unit ?? '',
        log.doseAmount ?? '',
        log.notes ? `"${String(log.notes).replace(/"/g, '""')}"` : '',
      ]);

      const csvContent = [headers.join(','), ...rows.map((r: any[]) => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `ped-dose-logs_all_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  // Group history logs by date (local date, not UTC)
  const logsByDate = recentLogs.reduce((acc: any, log: any) => {
    const dateParts = log.date.split('T')[0];
    if (!acc[dateParts]) acc[dateParts] = [];
    acc[dateParts].push(log);
    return acc;
  }, {});

  const sortedDates = Object.keys(logsByDate).sort().reverse();

  // Paginate by date group
  const totalPages    = Math.max(1, Math.ceil(sortedDates.length / HISTORY_PAGE_SIZE));
  const paginatedDates = sortedDates.slice(
    (historyCurrentPage - 1) * HISTORY_PAGE_SIZE,
    historyCurrentPage * HISTORY_PAGE_SIZE,
  );

  return (
    <div className="space-y-6">
      {/* ── Quick Log Form ─────────────────────────────────────────────────── */}
      <div className="bg-dark-800 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gold-500 mb-1">Log Today's Doses</h2>
            <p className="text-sm text-gray-400">
              {format(new Date(), 'EEEE, MMMM d, yyyy')}
            </p>
          </div>
          {!isCoachView && (
            <button
              onClick={() => setShowLogForm(true)}
              className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-2 px-6 rounded transition-colors"
            >
              + Log Dose
            </button>
          )}
        </div>

        {/* Today's Logs */}
        {logs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No doses logged for today</p>
            <p className="text-sm mt-2">Click "Log Dose" to add an entry</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log: any) => (
              <div key={log.logId} className="flex justify-between items-center bg-dark-700 rounded-lg p-4">
                <div>
                  <div className="font-semibold text-white">
                    {log.compound?.name} ({log.compound?.shortName})
                  </div>
                  <div className="text-sm text-gray-400">
                    {log.doseAmount}{log.compound?.unit}
                    {log.notes && ` • ${log.notes}`}
                  </div>
                </div>
                {!isCoachView && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditingLog(log);
                        setShowLogForm(true);
                      }}
                      className="text-blue-400 hover:text-blue-300 px-3 py-1 rounded"
                    >
                      Edit
                    </button>
                    {confirmDeleteId === log.logId ? (
                      <>
                        <span className="text-xs text-gray-400">Delete?</span>
                        <button
                          onClick={() => deleteLogMutation.mutate(log.logId)}
                          className="text-xs px-2 py-1 bg-red-600 hover:bg-red-500 text-white font-semibold rounded transition-colors"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-xs px-2 py-1 bg-dark-600 hover:bg-dark-500 border border-dark-500 text-gray-300 font-semibold rounded transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteId(log.logId)}
                        className="text-red-400 hover:text-red-300 px-3 py-1 rounded"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Dose Log History ───────────────────────────────────────────────── */}
      <div className="bg-dark-800 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
          <h2 className="text-xl font-bold text-white">Dose Log History</h2>
          {/* Export All — fetches every record with no date filter */}
          <button
            onClick={handleExportAll}
            disabled={isExporting}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-5 rounded text-sm transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {isExporting ? 'Exporting…' : 'Export All'}
          </button>
        </div>

        {/* Date range controls */}
        <div className="flex flex-wrap items-end gap-4 mb-4 pb-4 border-b border-dark-700">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">From</label>
            <input
              type="date"
              value={historyDateFrom}
              onChange={(e) => setHistoryDateFrom(e.target.value)}
              className="bg-dark-700 border border-dark-600 text-white rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">To</label>
            <input
              type="date"
              value={historyDateTo}
              onChange={(e) => setHistoryDateTo(e.target.value)}
              className="bg-dark-700 border border-dark-600 text-white rounded px-3 py-2 text-sm"
            />
          </div>
          <button
            onClick={handleApplyDateRange}
            className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-2 px-5 rounded text-sm transition-colors"
          >
            Apply
          </button>
          <span className="text-sm text-gray-500 self-center">
            {sortedDates.length} {sortedDates.length === 1 ? 'day' : 'days'} found
          </span>
        </div>

        {sortedDates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No dose logs for this date range</p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {paginatedDates.map((dateKey) => {
                const dateLogs   = logsByDate[dateKey];
                const totalDoses = dateLogs.length;
                const isExpanded = expandedDates.has(dateKey);

                return (
                  <div key={dateKey} className="border border-dark-700 rounded-lg overflow-hidden">
                    {/* ── Collapsed header (always visible) ── */}
                    <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-dark-700/60 transition-colors">
                      {/* Left: chevron + date (click to expand/collapse) */}
                      <div
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => toggleDate(dateKey)}
                      >
                        <svg
                          className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="font-semibold text-white">
                          {format(new Date(dateKey + 'T12:00:00'), 'EEEE, MMMM d, yyyy')}
                        </span>
                      </div>
                      {/* Right: Edit Day button + dose count */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {!isCoachView && (
                          <button
                            onClick={() => setEditingDayKey(dateKey)}
                            className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded hover:bg-dark-700/80 transition-colors"
                          >
                            Edit Day
                          </button>
                        )}
                        <span
                          className="text-sm text-gray-400 cursor-pointer"
                          onClick={() => toggleDate(dateKey)}
                        >
                          {totalDoses} dose{totalDoses !== 1 ? 's' : ''} logged
                        </span>
                      </div>
                    </div>

                    {/* ── Expanded compound rows ── */}
                    {isExpanded && (
                      <div className="border-t border-dark-700 divide-y divide-dark-700/50">
                        {dateLogs.map((log: any) => (
                          <div key={log.logId} className="flex justify-between items-center bg-dark-700/30 px-4 py-2.5">
                            <div className="text-sm">
                              <span className="text-white font-medium">{log.compound?.shortName}</span>
                              <span className="text-gray-300 ml-2">{log.doseAmount}{log.compound?.unit}</span>
                              {log.notes && <span className="text-gray-500 ml-2 italic">• {log.notes}</span>}
                            </div>
                            {!isCoachView && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditingLog(log);
                                    setShowLogForm(true);
                                  }}
                                  className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1"
                                >
                                  Edit
                                </button>
                                {confirmDeleteId === log.logId ? (
                                  <>
                                    <span className="text-xs text-gray-400">Delete?</span>
                                    <button
                                      onClick={() => deleteLogMutation.mutate(log.logId)}
                                      className="text-xs px-2 py-1 bg-red-600 hover:bg-red-500 text-white font-semibold rounded transition-colors"
                                    >
                                      Yes
                                    </button>
                                    <button
                                      onClick={() => setConfirmDeleteId(null)}
                                      className="text-xs px-2 py-1 bg-dark-600 hover:bg-dark-500 border border-dark-500 text-gray-300 font-semibold rounded transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => setConfirmDeleteId(log.logId)}
                                    className="text-red-400 hover:text-red-300 text-xs px-2 py-1"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                <p className="text-sm text-gray-400">
                  Showing days {(historyCurrentPage - 1) * HISTORY_PAGE_SIZE + 1}–{Math.min(historyCurrentPage * HISTORY_PAGE_SIZE, sortedDates.length)} of {sortedDates.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setHistoryCurrentPage(p => Math.max(1, p - 1))}
                    disabled={historyCurrentPage === 1}
                    className="px-3 py-1 text-sm rounded bg-dark-600 border border-dark-500 text-gray-300 hover:bg-dark-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Prev
                  </button>
                  <span className="text-sm text-gray-400">
                    Page {historyCurrentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setHistoryCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={historyCurrentPage === totalPages}
                    className="px-3 py-1 text-sm rounded bg-dark-600 border border-dark-500 text-gray-300 hover:bg-dark-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Day — opens the same LogFormModal, pre-set to the selected date.
          LogFormModal detects existing logs and enters "editing date" mode automatically. */}
      {editingDayKey && (
        <LogFormModal
          compounds={compounds}
          activeCycle={activeCycle ?? undefined}
          initialDate={editingDayKey}
          onClose={() => setEditingDayKey(null)}
          onSuccess={() => {
            setEditingDayKey(null);
            queryClient.invalidateQueries({ queryKey: ['ped-logs'] });
            queryClient.invalidateQueries({ queryKey: ['ped-logs-recent'] });
          }}
        />
      )}

      {/* Log Form Modal */}
      {showLogForm && (
        <LogFormModal
          compounds={compounds}
          activeCycle={activeCycle ?? undefined}
          editLog={editingLog}
          onClose={() => {
            setShowLogForm(false);
            setEditingLog(null);
          }}
          onSuccess={() => {
            setShowLogForm(false);
            setEditingLog(null);
            queryClient.invalidateQueries({ queryKey: ['ped-logs'] });
            queryClient.invalidateQueries({ queryKey: ['ped-logs-recent'] });
          }}
        />
      )}
    </div>
  );
};

export default PEDTracking;
